import {
  Box,
  Breadcrumbs,
  Container,
  Grid,
  ListItem,
  ListItemText,
  Typography,
  styled,
} from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { IComposableDynamicListingContext } from 'wisestep-api';
import { WsButton, WsColors, WsDrawer, WsTypoVariants, WsTypography } from 'wisestep-base';

import { AccessControl } from '../../../../components';
import { potentialRevenueTimePeriodList } from '../../../../features/composableAnalytics/DerivedCharts/PotentialRevenue/helper/potentialRevenueDashboardkeys';
import {
  ComposableAnalyticsDataAccessEnum,
  useComposableAnalyticsDataAccessHook,
} from '../../../../hooks/useComposableAnalyticsDataAccessHook';
import { ChartScope, DataSource } from '../../../composableAnalytics';
import { businessDefinitionTimePeriodList } from '../../../composableAnalytics/DerivedCharts/businessDefinition/helper/postgreBusinessDefinitionKeys';
import { jobsTimePeriodList } from '../../../composableAnalytics/DerivedCharts/Jobs/helper/postgreJobsDashboardKeys';
import { Index } from '../../../composableAnalytics/Widgets/helper/freeTextWidgetKeys';
import {
  postgreApplicationWidgetsByTimePeriod,
  freeTextWidget,
  postgreLeadChartsByTimePeriod,
  postgreClientNotesChartsByTimePeriod,
  postgreCvUploadChartsByTimePeriod,
  postgreEmailsChartsByTimePeriod,
  postgreJobsChartsByTimePeriod,
  postgreMappingsChartsByTimePeriod,
  postgreSmsWidgetsByTimePeriod,
  postgreSubmissionChartsByTimePeriod,
  potentialRevenueChartsByTimePeriod,
  postgreCallsWidgetsByTimePeriod,
  postgreCvViewWidgetsByTimePeriod,
  postgresFollowUpWidgetsByTimePeriod,
  postgreBusinessDefinitionChartsByTimePeriod,
  postgreRTRAnalyticsWidgetsByTimePeriod,
} from '../../helper/postgreDrawerChartsList';
import { generalTimePeriodList, reducedGeneralTimePeriodList } from '../../helper/timePeriodList';
import { useComposableDynamicListingContext } from '../../hooks/useComposableDynamicListingContext';

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 0,
  justifyContent: 'flex-start',
  width: '50vw',
  marginTop: '3vh',
}));

export const TabOption = styled(ListItem)(() => ({
  border: '4px solid',
  borderRadius: '4px',
  borderColor: WsColors.BorderGrey,
  cursor: 'pointer',
  padding: 10,
}));

const widgetCategories = [
  { label: 'Free Text Widget', value: 'free_text_widget' },
  { label: 'Calls', value: 'calls' },
  { label: 'SMS', value: 'sms' },
  { label: 'Jobs', value: 'jobs' },
  { label: 'Emails', value: 'emails' },
  { label: 'CV Views', value: 'cv_view' },
  { label: 'Client Notes', value: 'client_notes' },
  { label: 'Potential Revenue', value: 'revenue' },
  { label: 'Submissions', value: 'submissions' },
  { label: 'Mappings', value: 'mappings' },
  { label: 'CV Upload', value: 'cv_upload' },
  { label: 'Application Received', value: 'application_received' },
  { label: 'Follow Ups', value: 'follow_ups' },
  { label: 'Business Definition', value: 'business_definition' },
  { label: 'Leads', value: 'leads' },
  { label: 'RTR Analytics', value: 'rtr_analytics' },
];

interface breadcrumbsType {
  title: string;
  belongsToDrawerState: number;
}
const AddChartSelectionDrawer = ({
  openChartSelectionDrawer,
  setOpenChartSelectionDrawer,
}: {
  openChartSelectionDrawer: boolean;
  setOpenChartSelectionDrawer: Dispatch<SetStateAction<boolean>>;
  workspaceId: string;
}) => {
  const [drawerState, setDrawerState] = useState(0);
  const [breadcrumbsState, setBreadcrumbsState] = useState<breadcrumbsType[]>([
    {
      title: 'Home',
      belongsToDrawerState: -1,
    },
  ]);
  const [selectedAnalyticsIndex, setSelectedAnalyticsIndex] = useState('calls');
  const [selectedScope, setSelectedScope] = useState(ChartScope.WORKSPACE);
  const [widgets, setWidgets] = useState<JSX.Element>();
  const { access: hasDataAccess, owns: isAdmin } = useComposableAnalyticsDataAccessHook([
    ComposableAnalyticsDataAccessEnum.WORKSPACE_DATA,
    ComposableAnalyticsDataAccessEnum.TEAMS_DATA,
    ComposableAnalyticsDataAccessEnum.LOGGED_IN_USER_DATA,
  ]);
  const {
    businessDefinitionChartsKeysPostgre,
    potentialRevenueForBDefChartsKeys,
    leadByStatusChartsKeys,
  } = useComposableDynamicListingContext() as Required<IComposableDynamicListingContext>;

  const chartsDataAccess =
    hasDataAccess?.[0] || hasDataAccess?.[1] || hasDataAccess?.[2] || isAdmin;
  const filterScopesBasedOnAccess = useCallback(
    (scopes: { label: string; value: ChartScope }[]) => {
      if (isAdmin) return scopes;
      return scopes.filter(
        (scope) =>
          (scope.value == ChartScope.WORKSPACE && hasDataAccess?.[0]) ||
          (scope.value == ChartScope.TEAMS && hasDataAccess?.[1]) ||
          (scope.value == ChartScope.LOGGED_IN_USER && hasDataAccess?.[2])
      );
    },
    [hasDataAccess, isAdmin]
  );
  const postgreJobsCharts = useMemo(
    () => postgreJobsChartsByTimePeriod(selectedScope, jobsTimePeriodList),
    [selectedScope]
  );
  const onClickHandler = (category: { label: string; value: string }) => {
    setSelectedAnalyticsIndex(category.value);
    if (optionsState[category.value]?.dataSources) setDrawerStateUp();
    else if (category.value == Index.FREE_TEXT_WIDGET) {
      const { widgets } = optionsState[category.value].timePeriods[0];
      setWidgets(widgets);
      setDrawerState(4);
    } else setDrawerState(2);
    setBreadcrumbsState([...breadcrumbsState, { title: category.value, belongsToDrawerState: 0 }]);
  };
  const optionsState: Record<
    string,
    {
      scopes?: { label: string; value: ChartScope }[];
      timePeriods: {
        label: string;
        widgets: JSX.Element;
      }[];
      dataSources?: { label: string; value: DataSource }[];
    }
  > = useMemo(
    () => ({
      free_text_widget: {
        timePeriods: freeTextWidget(),
      },
      calls: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreCallsWidgetsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      sms: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreSmsWidgetsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      cv_view: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreCvViewWidgetsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      jobs: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Teams Data', value: ChartScope.TEAMS },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreJobsCharts,
      },
      emails: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreEmailsChartsByTimePeriod(selectedScope, generalTimePeriodList),
      },
      client_notes: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreClientNotesChartsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      revenue: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
          { label: 'Teams', value: ChartScope.TEAMS },
        ]),
        timePeriods: potentialRevenueChartsByTimePeriod(
          selectedScope,
          potentialRevenueTimePeriodList,
          potentialRevenueForBDefChartsKeys
        ),
      },
      submissions: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
          { label: 'Teams', value: ChartScope.TEAMS },
        ]),
        timePeriods: postgreSubmissionChartsByTimePeriod(selectedScope, generalTimePeriodList),
      },
      mappings: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
          { label: 'Teams', value: ChartScope.TEAMS },
        ]),
        timePeriods: postgreMappingsChartsByTimePeriod(selectedScope, generalTimePeriodList),
      },
      cv_upload: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreCvUploadChartsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      application_received: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
          { label: 'Teams', value: ChartScope.TEAMS },
        ]),
        timePeriods: postgreApplicationWidgetsByTimePeriod(selectedScope, jobsTimePeriodList),
      },
      follow_ups: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
          { label: 'Teams', value: ChartScope.TEAMS },
        ]),
        timePeriods: postgresFollowUpWidgetsByTimePeriod(selectedScope, generalTimePeriodList),
      },
      business_definition: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
        ]),
        timePeriods: postgreBusinessDefinitionChartsByTimePeriod(
          selectedScope,
          businessDefinitionTimePeriodList,
          businessDefinitionChartsKeysPostgre
        ),
      },
      leads: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace Data', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreLeadChartsByTimePeriod(
          selectedScope,
          jobsTimePeriodList,
          leadByStatusChartsKeys
        ),
      },
      rtr_analytics: {
        scopes: filterScopesBasedOnAccess([
          { label: 'Workspace', value: ChartScope.WORKSPACE },
          { label: 'Your Data', value: ChartScope.LOGGED_IN_USER },
        ]),
        timePeriods: postgreRTRAnalyticsWidgetsByTimePeriod(
          selectedScope,
          reducedGeneralTimePeriodList
        ),
      },
    }),
    [
      filterScopesBasedOnAccess,
      selectedScope,
      postgreJobsCharts,
      potentialRevenueForBDefChartsKeys,
      businessDefinitionChartsKeysPostgre,
      leadByStatusChartsKeys,
    ]
  );

  const handleBreadcrumbs = (clickedIndex: number, backLogic?: boolean) => {
    const breadcrumbsHeadIndex =
      clickedIndex +
      (optionsState[selectedAnalyticsIndex]?.dataSources || clickedIndex < 1 || backLogic ? 1 : 0);

    const newDrawerState =
      clickedIndex +
      (!optionsState[selectedAnalyticsIndex]?.dataSources
        ? clickedIndex == 0
          ? 2
          : backLogic
          ? clickedIndex == -1
            ? 1
            : 2
          : 1
        : 1);

    setBreadcrumbsState(breadcrumbsState.filter((_$, index) => index <= breadcrumbsHeadIndex));
    setDrawerState(newDrawerState);
  };

  const drawerOptionsUi = () => {
    switch (drawerState) {
      case 0:
        return widgetCategories.map((category, index) => (
          <Grid item xs={11} key={index}>
            <TabOption onClick={() => onClickHandler(category)}>
              <ListItemText>
                <WsTypography cursor="pointer" variant={WsTypoVariants.TITLE} bold>
                  {category.label}
                </WsTypography>
              </ListItemText>
            </TabOption>
          </Grid>
        ));
      case 2:
        return optionsState[selectedAnalyticsIndex]?.scopes?.map((text, index) => (
          <Grid item xs={11} key={index}>
            <TabOption
              onClick={() => {
                setSelectedScope(text.value);
                setDrawerStateUp();
                setBreadcrumbsState([
                  ...breadcrumbsState,
                  { title: text.value, belongsToDrawerState: 2 },
                ]);
              }}
            >
              <ListItemText>
                <WsTypography cursor="pointer" variant={WsTypoVariants.TITLE} bold>
                  {text.label}
                </WsTypography>
              </ListItemText>
            </TabOption>
          </Grid>
        ));
      case 3:
        return optionsState[selectedAnalyticsIndex]?.timePeriods.map((timePeriod, index) => (
          <Grid item xs={11} key={index}>
            <TabOption
              onClick={() => {
                setWidgets(timePeriod.widgets);
                setDrawerStateUp();
                setBreadcrumbsState([
                  ...breadcrumbsState,
                  { title: timePeriod.label, belongsToDrawerState: 3 },
                ]);
              }}
            >
              <ListItemText>
                <WsTypography cursor="pointer" variant={WsTypoVariants.TITLE} bold>
                  {timePeriod.label}
                </WsTypography>
              </ListItemText>
            </TabOption>
          </Grid>
        ));
      case 4:
        return (
          <Grid item xs={11}>
            {widgets}
          </Grid>
        );
    }
  };

  const setDrawerStateUp = () => setDrawerState((prev) => prev + 1);
  const setDrawerStateDown = () => {
    drawerState == 0
      ? setOpenChartSelectionDrawer(false)
      : handleBreadcrumbs(breadcrumbsState.length - 3, true);
  };

  const title = {
    0: 'Select Index',
    1: 'Select Data Source',
    2: 'Select Scope',
    3: 'Select Time Period',
    4: 'Select Widgets',
  };

  return (
    <WsDrawer
      title={chartsDataAccess ? title[drawerState] : 'Access Denied'}
      iconType="arrow"
      open={openChartSelectionDrawer}
      onClose={() => setDrawerStateDown()}
      size={!chartsDataAccess ? 'medium' : 'small'}
      headerSize="medium"
    >
      {!chartsDataAccess ? (
        <Container sx={{ textAlign: 'center', mt: '20%' }}>
          <AccessControl />
        </Container>
      ) : (
        <Box pb={8}>
          <Grid mt={1} spacing={1} container justifyContent="center">
            <Grid item xs={11}>
              <Breadcrumbs aria-label="breadcrumb">
                {breadcrumbsState.map((state, index) => (
                  <Typography
                    key={index}
                    onClick={() => handleBreadcrumbs(state.belongsToDrawerState)}
                    variant={'subtitle1'}
                    sx={{
                      cursor: 'pointer',
                      color: WsColors.TheBlues400,
                      '&:hover': {
                        color: WsColors.TheBlues500,
                      },
                    }}
                  >
                    {state.title}
                  </Typography>
                ))}
              </Breadcrumbs>
            </Grid>
          </Grid>
          <Grid container justifyContent="center" mt={0.1} spacing={4}>
            {drawerOptionsUi()}
          </Grid>
          <DrawerFooter>
            <Grid
              item
              xs={7.2}
              py={2}
              px={12}
              display="flex"
              justifyContent="space-between"
              bgcolor={WsColors.BackGround}
            >
              <WsButton
                variant="contained"
                size="large"
                onClick={() => setOpenChartSelectionDrawer(false)}
              >
                Cancel
              </WsButton>
              <WsButton
                variant="contained"
                size="large"
                onClick={() => setOpenChartSelectionDrawer(false)}
              >
                Preview
              </WsButton>
            </Grid>
          </DrawerFooter>
        </Box>
      )}
    </WsDrawer>
  );
};

export default AddChartSelectionDrawer;
